import {
  POSTGRES_JOB_STATUSES,
  type DeploymentEnvironment,
  type PostgresJob,
  type PostgresJobFilters,
  type PostgresJobStatus,
} from '@dev-console/types';
import { Lane } from '@shared/jobs/constants';
import { Pod } from '@shared/pods/constants';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  JobsDbEnvironmentBadge,
  JobsDbEnvironmentToggle,
  jobsDbEnvironmentParamDef,
} from '@/components/jobs/jobs-db-environment-toggle';
import { EmptyState, ErrorBanner } from '@/components/ui/state-views';
import { ApiError } from '@/config/api';
import { discardPostgresJobs, getPostgresJobNames, getPostgresJobs, redrivePostgresJobs } from '@/config/api-helpers';
import { DEV_CONSOLE_AXIOM_QUERY_BASE_URL, DEV_CONSOLE_AXIOM_WORKER_LOGS_DATASET } from '@/config/devConsole';
import { useSearchParamsState } from '@/hooks/useSearchParamsState';
import { truncate } from '@/lib/utils';

export const meta = {
  label: 'Jobs DB',
  order: 13,
  section: 'Jobs',
};

const STATUSES: (PostgresJobStatus | '')[] = ['', ...POSTGRES_JOB_STATUSES];
const LANES: (Lane | '')[] = ['', ...Object.values(Lane)];
const PODS: (Pod | '')[] = ['', ...Object.values(Pod)];
const FALLBACK_TIME_ZONE_LABEL = 'Local time';

const STATUS_BADGE_CLASSES: Record<PostgresJobStatus, string> = {
  SCHEDULED: 'bg-purple-100 text-purple-800',
  PENDING: 'bg-blue-100 text-blue-800',
  RUNNING: 'bg-amber-100 text-amber-800',
  SUCCEEDED: 'bg-green-100 text-green-800',
  FAILED: 'bg-red-100 text-red-800',
  DISCARDED: 'bg-neutral-100 text-neutral-600',
};

const filterParamDefs = {
  environment: jobsDbEnvironmentParamDef,
  name: { default: '' },
  pod: { default: '' },
  status: { default: '' },
  lane: { default: '' },
  createdAfter: { default: '' },
  createdBefore: { default: '' },
};

function matchesFilters(job: PostgresJob, filters: PostgresJobFilters): boolean {
  if (filters.name !== undefined && job.name !== filters.name) return false;
  if (filters.pod !== undefined && job.pod !== filters.pod) return false;
  if (filters.status !== undefined && job.status !== filters.status) return false;
  if (filters.lane !== undefined && job.lane !== filters.lane) return false;
  if (filters.createdAfter !== undefined && job.createdAt < filters.createdAfter) return false;
  if (filters.createdBefore !== undefined && job.createdAt > filters.createdBefore) return false;
  return true;
}

function isOverdue(job: PostgresJob): boolean {
  return job.status === 'SCHEDULED' && new Date(job.runAt).getTime() <= Date.now();
}

function getBrowserTimeZone(): string | undefined {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

function getTimeZoneLabel(timeZone: string | undefined): string {
  if (timeZone == null) return FALLBACK_TIME_ZONE_LABEL;

  return (
    new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'short',
    })
      .formatToParts(new Date())
      .find((part) => part.type === 'timeZoneName')?.value ?? timeZone
  );
}

function formatJobTimestamp(timestamp: string, timeZone: string | undefined): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    timeZone,
  }).format(new Date(timestamp));
}

function escapeAplString(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

function buildAxiomUrl(environment: DeploymentEnvironment, whereFilter: string): string {
  const aplQuery = `['${DEV_CONSOLE_AXIOM_WORKER_LOGS_DATASET}']\n| where app_env == "${environment}" and ${whereFilter}`;
  const initForm: Record<string, string> = { apl: aplQuery };

  return `${DEV_CONSOLE_AXIOM_QUERY_BASE_URL}?initForm=${encodeURIComponent(JSON.stringify(initForm))}`;
}

function buildPostgresJobIdAxiomUrl(environment: DeploymentEnvironment, jobId: string): string {
  return buildAxiomUrl(environment, `['context.jobId'] == "${escapeAplString(jobId)}"`);
}

function buildPostgresTraceIdAxiomUrl(environment: DeploymentEnvironment, traceId: string): string {
  const escapedTraceId = escapeAplString(traceId);

  return buildAxiomUrl(
    environment,
    `(['context.traceId'] == "${escapedTraceId}" or ['context.upstreamTraceId'] == "${escapedTraceId}")`
  );
}

function buildJobSchedulesLink(jobName: string, environment: DeploymentEnvironment): string {
  const sp = new URLSearchParams({ jobName });
  if (environment !== jobsDbEnvironmentParamDef.default) sp.set('environment', environment);
  return `/job-schedules?${sp}`;
}

export const PostgresJobsPage = () => {
  const {
    environment: [environment, setEnvironment],
    name: [nameFilter, setNameFilter],
    pod: [podFilter, setPodFilter],
    status: [statusFilter, setStatusFilter],
    lane: [laneFilter, setLaneFilter],
    createdAfter: [createdAfterFilter, setCreatedAfterFilter],
    createdBefore: [createdBeforeFilter, setCreatedBeforeFilter],
  } = useSearchParamsState(filterParamDefs);

  const [jobs, setJobs] = useState<PostgresJob[]>([]);
  const [jobNames, setJobNames] = useState<string[]>([]);
  const [jobNamesError, setJobNamesError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [appliedFilters, setAppliedFilters] = useState<PostgresJobFilters>({});
  const [pageIndex, setPageIndex] = useState(0);
  const [pageCursors, setPageCursors] = useState<(string | undefined)[]>([undefined]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [selectedJobIds, setSelectedJobIds] = useState<string[]>([]);
  const [jobsToRedrive, setJobsToRedrive] = useState<PostgresJob[]>([]);
  const [jobsToDiscard, setJobsToDiscard] = useState<PostgresJob[]>([]);
  const [redriving, setRedriving] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const latestLoadRef = useRef(0);

  const searchFilters = useMemo<PostgresJobFilters>(
    () => ({
      name: nameFilter.trim() || undefined,
      pod: podFilter || undefined,
      status: (statusFilter as PostgresJobStatus) || undefined,
      lane: (laneFilter as Lane) || undefined,
      createdAfter: createdAfterFilter ? new Date(createdAfterFilter).toISOString() : undefined,
      createdBefore: createdBeforeFilter ? new Date(createdBeforeFilter).toISOString() : undefined,
    }),
    [nameFilter, podFilter, statusFilter, laneFilter, createdAfterFilter, createdBeforeFilter]
  );

  // Keep a name from the URL selectable even if it has no rows (e.g. all of them aged out of retention).
  const jobNameOptions = useMemo(
    () => (nameFilter && !jobNames.includes(nameFilter) ? [nameFilter, ...jobNames] : jobNames),
    [jobNames, nameFilter]
  );
  const failedJobs = useMemo(() => jobs.filter((job) => job.status === 'FAILED'), [jobs]);
  const selectedJobIdSet = useMemo(() => new Set(selectedJobIds), [selectedJobIds]);
  const browserTimeZone = getBrowserTimeZone();
  const browserTimeZoneLabel = getTimeZoneLabel(browserTimeZone);

  const loadPage = useCallback(
    async (filters: PostgresJobFilters, cursor?: string) => {
      const loadId = ++latestLoadRef.current;
      setLoading(true);
      setError(null);
      setHasSearched(true);
      setExpandedId(null);
      setSelectedJobIds([]);
      try {
        const data = await getPostgresJobs(environment, {
          ...filters,
          cursor,
        });
        if (loadId !== latestLoadRef.current) return false;
        setJobs(data.jobs);
        setNextCursor(data.nextCursor);
        return true;
      } catch (err) {
        if (loadId !== latestLoadRef.current) return false;
        setError(err instanceof Error ? err.message : 'Failed to load jobs');
        return false;
      } finally {
        if (loadId === latestLoadRef.current) setLoading(false);
      }
    },
    [environment]
  );

  const handleSearch = useCallback(async () => {
    setAppliedFilters(searchFilters);
    setPageIndex(0);
    setPageCursors([undefined]);
    setNextCursor(null);
    setJobs([]);
    await loadPage(searchFilters);
  }, [loadPage, searchFilters]);

  const handleNextPage = useCallback(async () => {
    if (nextCursor == null || loading) {
      return;
    }

    const cursor = nextCursor;
    const loaded = await loadPage(appliedFilters, cursor);
    if (!loaded) {
      return;
    }

    setPageCursors((current) => [...current.slice(0, pageIndex + 1), cursor]);
    setPageIndex((current) => current + 1);
  }, [appliedFilters, loadPage, loading, nextCursor, pageIndex]);

  const handlePreviousPage = useCallback(async () => {
    if (pageIndex === 0 || loading) {
      return;
    }

    const previousPageIndex = pageIndex - 1;
    const loaded = await loadPage(appliedFilters, pageCursors[previousPageIndex]);
    if (loaded) {
      setPageIndex(previousPageIndex);
    }
  }, [appliedFilters, loadPage, loading, pageCursors, pageIndex]);

  useEffect(() => {
    setJobsToRedrive([]);
    setJobsToDiscard([]);
    setSuccessMessage(null);
    setJobNames([]);
    setJobNamesError(false);
    handleSearch();

    let cancelled = false;
    getPostgresJobNames(environment)
      .then((data) => {
        if (!cancelled) setJobNames(data.names);
      })
      .catch(() => {
        if (!cancelled) setJobNamesError(true);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [environment]);

  const handleRedrive = useCallback(async () => {
    if (jobsToRedrive.length === 0) {
      return;
    }

    setRedriving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const result = await redrivePostgresJobs(
        environment,
        jobsToRedrive.map((job) => job.id)
      );
      const redrivenJobsById = new Map(result.succeeded.map(({ job }) => [job.id, job]));
      const redrivenJobIds = new Set(redrivenJobsById.keys());

      // Redriving leaves created_at alone, so a job keeps its place unless it no longer matches the filters.
      setJobs((current) =>
        current.flatMap((job) => {
          const redrivenJob = redrivenJobsById.get(job.id);
          if (redrivenJob == null) return [job];
          return matchesFilters(redrivenJob, appliedFilters) ? [redrivenJob] : [];
        })
      );
      setExpandedId((current) => (current != null && redrivenJobIds.has(current) ? null : current));
      setJobsToRedrive([]);
      setSelectedJobIds([]);

      if (result.succeeded.length > 0) {
        setSuccessMessage(
          `Redrove ${result.succeeded.length} of ${jobsToRedrive.length} selected job(s). Successful jobs are pending with fresh attempt budgets.`
        );
      }
      if (result.failed.length > 0) {
        setError(
          `${result.failed.length} job(s) could not be redriven: ${result.failed
            .map((failure) => `${failure.jobId}: ${failure.message}`)
            .join('; ')}`
        );
      }
    } catch (err) {
      const apiMessage = err instanceof ApiError && 'message' in err.body ? err.body.message : null;
      setJobsToRedrive([]);
      setError(apiMessage ?? (err instanceof Error ? err.message : 'Failed to redrive jobs'));
    } finally {
      setRedriving(false);
    }
  }, [appliedFilters, environment, jobsToRedrive]);

  const handleDiscard = useCallback(async () => {
    if (jobsToDiscard.length === 0) {
      return;
    }

    setDiscarding(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const result = await discardPostgresJobs(
        environment,
        jobsToDiscard.map((job) => job.id)
      );
      const discardedJobsById = new Map(result.succeeded.map(({ job }) => [job.id, job]));
      const discardedJobIds = new Set(discardedJobsById.keys());

      setJobs((current) =>
        current.flatMap((job) => {
          const discardedJob = discardedJobsById.get(job.id);
          if (discardedJob == null) return [job];
          return matchesFilters(discardedJob, appliedFilters) ? [discardedJob] : [];
        })
      );
      setExpandedId((current) => (current != null && discardedJobIds.has(current) ? null : current));
      setJobsToDiscard([]);
      setSelectedJobIds([]);

      if (result.succeeded.length > 0) {
        setSuccessMessage(
          `Discarded ${result.succeeded.length} of ${jobsToDiscard.length} selected job(s). Successful jobs are marked DISCARDED in jobs_db.`
        );
      }
      if (result.failed.length > 0) {
        setError(
          `${result.failed.length} job(s) could not be discarded: ${result.failed
            .map((failure) => `${failure.jobId}: ${failure.message}`)
            .join('; ')}`
        );
      }
    } catch (err) {
      const apiMessage = err instanceof ApiError && 'message' in err.body ? err.body.message : null;
      setJobsToDiscard([]);
      setError(apiMessage ?? (err instanceof Error ? err.message : 'Failed to discard jobs'));
    } finally {
      setDiscarding(false);
    }
  }, [appliedFilters, environment, jobsToDiscard]);

  return (
    <div>
      <div className='mb-4 flex flex-wrap items-center justify-between gap-3'>
        <div className='flex items-center gap-2'>
          <h2 className='font-mediumbold text-xl text-neutral-900'>Jobs DB</h2>
          <JobsDbEnvironmentBadge environment={environment} />
        </div>
        <JobsDbEnvironmentToggle value={environment} onChange={setEnvironment} />
      </div>

      {/* Filters */}
      <div className='mb-4 rounded-md border border-neutral-200 bg-neutral-50 p-4'>
        <div className='grid grid-cols-1 gap-3 md:grid-cols-3 lg:grid-cols-6'>
          <div>
            <label className='mb-1 block text-xs font-medium text-neutral-600'>Name</label>
            <select
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
              className='w-full rounded-md border border-neutral-300 px-3 py-1.5 text-sm text-neutral-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500'
            >
              <option value=''>All jobs</option>
              {jobNameOptions.map((jobName) => (
                <option key={jobName} value={jobName}>
                  {jobName}
                </option>
              ))}
            </select>
            {jobNamesError && <p className='mt-1 text-xs text-red-600'>Failed to load job names</p>}
          </div>
          <div>
            <label className='mb-1 block text-xs font-medium text-neutral-600'>Pod</label>
            <select
              value={podFilter}
              onChange={(e) => setPodFilter(e.target.value)}
              className='w-full rounded-md border border-neutral-300 px-3 py-1.5 text-sm text-neutral-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500'
            >
              {PODS.map((pod) => (
                <option key={pod} value={pod}>
                  {pod || 'All pods'}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className='mb-1 block text-xs font-medium text-neutral-600'>Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className='w-full rounded-md border border-neutral-300 px-3 py-1.5 text-sm text-neutral-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500'
            >
              {STATUSES.map((status) => (
                <option key={status} value={status}>
                  {status || 'All statuses'}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className='mb-1 block text-xs font-medium text-neutral-600'>Lane</label>
            <select
              value={laneFilter}
              onChange={(e) => setLaneFilter(e.target.value)}
              className='w-full rounded-md border border-neutral-300 px-3 py-1.5 text-sm text-neutral-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500'
            >
              {LANES.map((lane) => (
                <option key={lane} value={lane}>
                  {lane || 'All lanes'}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className='mb-1 block text-xs font-medium text-neutral-600'>Created After</label>
            <input
              type='datetime-local'
              value={createdAfterFilter}
              onChange={(e) => setCreatedAfterFilter(e.target.value)}
              className='w-full rounded-md border border-neutral-300 px-3 py-1.5 text-sm text-neutral-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500'
            />
          </div>
          <div>
            <label className='mb-1 block text-xs font-medium text-neutral-600'>Created Before</label>
            <input
              type='datetime-local'
              value={createdBeforeFilter}
              onChange={(e) => setCreatedBeforeFilter(e.target.value)}
              className='w-full rounded-md border border-neutral-300 px-3 py-1.5 text-sm text-neutral-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500'
            />
          </div>
        </div>
        <div className='mt-3 flex items-center justify-end gap-2'>
          <button
            onClick={handleSearch}
            disabled={loading}
            className='rounded-md bg-neutral-900 px-4 py-1.5 text-sm font-medium text-white hover:bg-neutral-800 disabled:cursor-not-allowed disabled:opacity-50'
          >
            {loading ? 'Applying...' : 'Apply Filters'}
          </button>
          <button
            onClick={() => {
              setNameFilter('');
              setPodFilter('');
              setStatusFilter('');
              setLaneFilter('');
              setCreatedAfterFilter('');
              setCreatedBeforeFilter('');
            }}
            className='rounded-md border border-neutral-200 px-4 py-1.5 text-sm text-neutral-700 hover:bg-neutral-50'
          >
            Clear
          </button>
        </div>
      </div>

      {error != null && <ErrorBanner className='mb-4' message={error} onRetry={handleSearch} />}
      {successMessage != null && (
        <div className='mb-4 rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800'>
          {successMessage}
        </div>
      )}

      {!loading && hasSearched && error == null && jobs.length === 0 && (
        <EmptyState message='No jobs found matching your filters.' />
      )}

      {jobs.length > 0 && (
        <div className='overflow-x-auto rounded-md border border-neutral-200'>
          <div className='flex flex-wrap items-center justify-between gap-3 border-b border-neutral-200 bg-white px-4 py-3'>
            <span className='text-sm text-neutral-600'>
              {selectedJobIds.length} selected · {failedJobs.length} failed job(s) on this page
            </span>
            <div className='flex flex-wrap items-center gap-2'>
              <button
                type='button'
                onClick={() => setSelectedJobIds(failedJobs.map((job) => job.id))}
                disabled={failedJobs.length === 0 || selectedJobIds.length === failedJobs.length}
                className='rounded-md border border-neutral-200 px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-50'
              >
                Select all failed jobs on this page
              </button>
              <button
                type='button'
                onClick={() => setSelectedJobIds([])}
                disabled={selectedJobIds.length === 0}
                className='rounded-md border border-neutral-200 px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-50'
              >
                Clear selection
              </button>
              <button
                type='button'
                onClick={() => setJobsToRedrive(failedJobs.filter((job) => selectedJobIdSet.has(job.id)))}
                disabled={selectedJobIds.length === 0}
                className='rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50'
              >
                Redrive selected ({selectedJobIds.length})
              </button>
              <button
                type='button'
                onClick={() => setJobsToDiscard(failedJobs.filter((job) => selectedJobIdSet.has(job.id)))}
                disabled={selectedJobIds.length === 0}
                className='rounded-md bg-neutral-700 px-3 py-2 text-sm font-medium text-white hover:bg-neutral-800 disabled:cursor-not-allowed disabled:opacity-50'
              >
                Discard selected ({selectedJobIds.length})
              </button>
            </div>
          </div>
          <table className='w-full text-left text-sm'>
            <thead className='border-b border-neutral-200 bg-neutral-50'>
              <tr>
                <th className='w-12 px-3 py-2.5 font-medium text-neutral-700'>Select</th>
                <th className='px-3 py-2.5 font-medium text-neutral-700'>Name</th>
                <th className='px-3 py-2.5 font-medium text-neutral-700'>Pod</th>
                <th className='px-3 py-2.5 font-medium text-neutral-700'>Lane</th>
                <th className='px-3 py-2.5 font-medium text-neutral-700'>Status</th>
                <th className='px-3 py-2.5 font-medium text-neutral-700'>Attempts</th>
                <th className='whitespace-nowrap px-3 py-2.5 font-medium text-neutral-700'>
                  Run At <span className='text-xs font-normal text-neutral-500'>({browserTimeZoneLabel})</span>
                </th>
                <th className='whitespace-nowrap px-3 py-2.5 font-medium text-neutral-700'>
                  Created <span className='text-xs font-normal text-neutral-500'>({browserTimeZoneLabel})</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((job) => {
                const isExpanded = expandedId === job.id;

                return (
                  <Fragment key={job.id}>
                    <tr
                      onClick={() => setExpandedId(isExpanded ? null : job.id)}
                      className={`cursor-pointer border-b border-neutral-100 hover:bg-neutral-50 ${
                        isExpanded ? 'bg-blue-50' : ''
                      }`}
                    >
                      <td className='px-3 py-2.5'>
                        <input
                          type='checkbox'
                          aria-label={`Select ${job.name} (${job.id})`}
                          checked={selectedJobIdSet.has(job.id)}
                          disabled={job.status !== 'FAILED'}
                          onClick={(event) => event.stopPropagation()}
                          onChange={(event) => {
                            setSelectedJobIds((current) =>
                              event.target.checked
                                ? [...current, job.id]
                                : current.filter((selectedJobId) => selectedJobId !== job.id)
                            );
                          }}
                          className='size-4 rounded border-neutral-300 text-blue-600 disabled:opacity-40'
                        />
                      </td>
                      <td className='px-3 py-2.5 font-mono text-xs text-neutral-800'>{job.name}</td>
                      <td className='px-3 py-2.5 text-neutral-600'>{job.pod}</td>
                      <td className='px-3 py-2.5 text-neutral-600'>{job.lane}</td>
                      <td className='px-3 py-2.5'>
                        <span
                          className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${
                            STATUS_BADGE_CLASSES[job.status]
                          }`}
                        >
                          {job.status}
                        </span>
                      </td>
                      <td className='px-3 py-2.5 text-neutral-600'>
                        {job.attemptCount}/{job.maxAttempts}
                      </td>
                      <td className='whitespace-nowrap px-3 py-2.5 text-xs text-neutral-600'>
                        {formatJobTimestamp(job.runAt, browserTimeZone)}
                        {isOverdue(job) && <span className='ml-1 font-medium text-amber-700'>overdue</span>}
                      </td>
                      <td className='whitespace-nowrap px-3 py-2.5 text-xs text-neutral-600'>
                        {formatJobTimestamp(job.createdAt, browserTimeZone)}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className='border-b border-neutral-100 bg-neutral-50'>
                        <td colSpan={8} className='px-6 py-4'>
                          <div className='space-y-3'>
                            <div className='flex flex-wrap gap-4 text-xs text-neutral-600'>
                              <span>
                                <span className='font-medium text-neutral-500'>Job ID:</span>{' '}
                                <a
                                  href={buildPostgresJobIdAxiomUrl(environment, job.id)}
                                  target='_blank'
                                  rel='noreferrer'
                                  className='font-mono text-blue-600 hover:underline'
                                >
                                  {job.id}
                                </a>
                              </span>
                              {job.traceId != null && (
                                <span>
                                  <span className='font-medium text-neutral-500'>Trace ID:</span>{' '}
                                  <a
                                    href={buildPostgresTraceIdAxiomUrl(environment, job.traceId)}
                                    target='_blank'
                                    rel='noreferrer'
                                    className='font-mono text-blue-600 hover:underline'
                                  >
                                    {truncate(job.traceId, 32)}
                                  </a>
                                </span>
                              )}
                              <span>
                                <span className='font-medium text-neutral-500'>Updated:</span>{' '}
                                {formatJobTimestamp(job.updatedAt, browserTimeZone)}
                              </span>
                              {job.lastAttemptedAt && (
                                <span>
                                  <span className='font-medium text-neutral-500'>Last Attempted:</span>{' '}
                                  {formatJobTimestamp(job.lastAttemptedAt, browserTimeZone)}
                                </span>
                              )}
                              {job.scheduleId && (
                                <span>
                                  <span className='font-medium text-neutral-500'>Schedule:</span>{' '}
                                  <Link
                                    to={buildJobSchedulesLink(job.name, environment)}
                                    className='font-mono text-blue-600 hover:underline'
                                  >
                                    {truncate(job.scheduleId, 32)}
                                  </Link>
                                </span>
                              )}
                              {job.dispatchLeaseExpiresAt && (
                                <span>
                                  <span className='font-medium text-neutral-500'>Dispatch Lease Expires:</span>{' '}
                                  {formatJobTimestamp(job.dispatchLeaseExpiresAt, browserTimeZone)}
                                </span>
                              )}
                            </div>
                            {job.lastError && (
                              <div>
                                <h4 className='mb-1 text-xs font-medium text-neutral-500'>Last Error</h4>
                                <p className='whitespace-pre-wrap text-sm text-red-700'>{job.lastError}</p>
                              </div>
                            )}
                            {job.status === 'FAILED' && (
                              <div className='flex flex-wrap gap-2'>
                                <button
                                  type='button'
                                  onClick={() => {
                                    setSuccessMessage(null);
                                    setJobsToRedrive([job]);
                                  }}
                                  className='rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700'
                                >
                                  Redrive job
                                </button>
                                <button
                                  type='button'
                                  onClick={() => {
                                    setSuccessMessage(null);
                                    setJobsToDiscard([job]);
                                  }}
                                  className='rounded-md bg-neutral-700 px-4 py-2 text-sm font-medium text-white hover:bg-neutral-800'
                                >
                                  Discard job
                                </button>
                              </div>
                            )}
                            <div>
                              <h4 className='mb-1 text-xs font-medium text-neutral-500'>Payload</h4>
                              <pre className='max-h-80 overflow-auto rounded-md bg-neutral-900 p-4 text-xs text-neutral-100'>
                                {JSON.stringify(job.payload, null, 2)}
                              </pre>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
          <div className='flex items-center justify-between border-t border-neutral-100 px-4 py-3'>
            <button
              type='button'
              onClick={handlePreviousPage}
              disabled={loading || pageIndex === 0}
              className='rounded-md border border-neutral-200 px-3 py-2 text-sm text-neutral-700 disabled:cursor-not-allowed disabled:opacity-50'
            >
              Previous
            </button>
            <span className='text-sm text-neutral-600'>Page {pageIndex + 1}</span>
            <button
              type='button'
              onClick={handleNextPage}
              disabled={loading || nextCursor == null}
              className='rounded-md border border-neutral-200 px-3 py-2 text-sm text-neutral-700 disabled:cursor-not-allowed disabled:opacity-50'
            >
              Next
            </button>
          </div>
        </div>
      )}

      {!hasSearched && !loading && <EmptyState message='Apply filters to search jobs.' />}

      {jobsToRedrive.length > 0 && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40'>
          <div className='mx-4 w-full max-w-lg rounded-lg bg-white shadow-xl'>
            <div className='border-b border-neutral-200 px-6 py-4'>
              <h3 className='text-lg font-medium text-neutral-900'>
                Redrive {jobsToRedrive.length} Postgres Job(s) in {environment}
              </h3>
            </div>
            <div className='space-y-3 px-6 py-4 text-sm text-neutral-700'>
              <p>
                This will reset the attempt count and enqueue {jobsToRedrive.length} failed {environment} job(s) to run
                again immediately.
              </p>
              <div className='max-h-48 overflow-auto rounded-md border border-neutral-200'>
                {jobsToRedrive.map((job) => (
                  <div key={job.id} className='border-b border-neutral-100 px-3 py-2 last:border-b-0'>
                    <div className='font-mono text-xs text-neutral-800'>{job.name}</div>
                    <div className='font-mono text-xs text-neutral-500'>{job.id}</div>
                  </div>
                ))}
              </div>
              <p className='text-amber-700'>Only continue after confirming the underlying failure is safe to retry.</p>
            </div>
            <div className='flex justify-end gap-2 border-t border-neutral-200 px-6 py-4'>
              <button
                type='button'
                onClick={() => setJobsToRedrive([])}
                disabled={redriving}
                className='rounded-md border border-neutral-200 px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50 disabled:opacity-50'
              >
                Cancel
              </button>
              <button
                type='button'
                onClick={handleRedrive}
                disabled={redriving}
                className='rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50'
              >
                {redriving ? 'Redriving...' : `Redrive ${jobsToRedrive.length} job(s)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {jobsToDiscard.length > 0 && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40'>
          <div className='mx-4 w-full max-w-lg rounded-lg bg-white shadow-xl'>
            <div className='border-b border-neutral-200 px-6 py-4'>
              <h3 className='text-lg font-medium text-neutral-900'>
                Discard {jobsToDiscard.length} Postgres Job(s) in {environment}
              </h3>
            </div>
            <div className='space-y-3 px-6 py-4 text-sm text-neutral-700'>
              <p>
                This will mark {jobsToDiscard.length} failed job(s) as DISCARDED in the {environment} jobs_db.
              </p>
              <div className='max-h-48 overflow-auto rounded-md border border-neutral-200'>
                {jobsToDiscard.map((job) => (
                  <div key={job.id} className='border-b border-neutral-100 px-3 py-2 last:border-b-0'>
                    <div className='font-mono text-xs text-neutral-800'>{job.name}</div>
                    <div className='font-mono text-xs text-neutral-500'>{job.id}</div>
                  </div>
                ))}
              </div>
              <p className='text-amber-700'>Only continue after confirming the job should not be retried.</p>
            </div>
            <div className='flex justify-end gap-2 border-t border-neutral-200 px-6 py-4'>
              <button
                type='button'
                onClick={() => setJobsToDiscard([])}
                disabled={discarding}
                className='rounded-md border border-neutral-200 px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50 disabled:opacity-50'
              >
                Cancel
              </button>
              <button
                type='button'
                onClick={handleDiscard}
                disabled={discarding}
                className='rounded-md bg-neutral-700 px-4 py-2 text-sm font-medium text-white hover:bg-neutral-800 disabled:cursor-not-allowed disabled:opacity-50'
              >
                {discarding ? 'Discarding...' : `Discard ${jobsToDiscard.length} job(s)`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
